/**
 * Remembers the iframe URL parameters of a Seller start.
 *
 * The Seller iframe auto-login (App.tsx) ends in `navigate(…, { replace:
 * true })`. That redirect carries `?lang=` and the colour parameters
 * across, and `lib/keep-shop-language.ts` keeps `lang` on the URL through
 * later in-app navigations, but the storage is still what makes them
 * survive a reload: that lands on a URL that never had the colours and, in
 * a fresh SPA session, no memory of the language. So the boot step records
 * those values in `sessionStorage` — per tab and origin, gone when the tab
 * closes — and `lib/theme.ts`, `lib/i18n.ts` and the language keeper fall
 * back to them when the URL carries nothing.
 *
 * Do not delete this because the redirect preserves the parameters: the two
 * cover different moments. The redirect covers the first navigation, the
 * storage covers every page load after it.
 *
 * Only a start WITH credentials in the URL writes (`isIframe=true` plus
 * `key`), and it writes exactly the URL values: a key missing from the URL
 * is removed from storage, so an embed without colours does not inherit the
 * colours of an earlier embed in the same tab. Direct Sale never writes and
 * never reads: it is a single route without a replace navigation, its URL
 * keeps its values, and a Direct Sale iframe starting later on the same page
 * must not clobber the Seller values of a sibling iframe (both share the
 * tab's sessionStorage). Nothing here throws — a browser that blocks storage
 * (private mode, embedder policy) just leaves the fallback empty.
 */

const STORAGE_KEY = 'designdeck.iframe-params';

/**
 * DesignDeck's own surfaces: the admin area, the login page that precedes
 * any customer, the onboarding wizard and the shop wizard. They show the
 * reference colours and stay single-language — neither the customer's
 * branding nor the shop's `lang` applies there.
 */
const REFERENCE_PATH_PREFIXES = [
  '/admin',
  '/login',
  '/onboarding',
  '/shop-wizard',
];

export function isReferencePath(pathname: string): boolean {
  return REFERENCE_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

/**
 * The URL parameters worth remembering, under their URL names. `embedTheme`
 * selects an embedding theme (lib/embed-theme.ts), the layout keys set single
 * layout values (lib/embed-layout.ts); both have to survive the same redirect
 * and reloads as the colours.
 */
const PARAM_KEYS = [
  'lang',
  'primaryColor',
  'paperColor',
  'inkColor',
  'embedTheme',
  'density',
  'radius',
  'tileMin',
  'chrome',
  'logo',
] as const;

export type IframeParamKey = (typeof PARAM_KEYS)[number];

/** Raw URL values, unvalidated — the readers validate for their own use. */
export type IframeParams = Partial<Record<IframeParamKey, string>>;

/** True for the Direct Sale route, which neither writes nor reads storage. */
export function isDirectSalePath(pathname: string): boolean {
  return pathname === '/direct-sale' || pathname.startsWith('/direct-sale/');
}

/** A Seller iframe start: auto-login credentials in the URL (see App.tsx). */
function isSellerIframeStart(params: URLSearchParams): boolean {
  return params.get('isIframe') === 'true' && !!params.get('key');
}

/**
 * Records `lang` and the colour parameters of a Seller iframe start. Call
 * once at boot, before the language and the branding are resolved. A start
 * without credentials — reload, internal link, Direct Sale — leaves storage
 * untouched.
 */
export function recordIframeParamsFromUrl(): void {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  if (!isSellerIframeStart(params)) return;

  const recorded: IframeParams = {};
  for (const key of PARAM_KEYS) {
    const value = params.get(key);
    if (value !== null) recorded[key] = value;
  }

  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(recorded));
  } catch {
    // Storage blocked (private mode, embedder policy): the URL values still
    // apply for this page view, they just do not survive the redirect.
  }
}

/** True when this tab recorded a Seller iframe start (see above). */
export function hasRecordedIframeStart(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(STORAGE_KEY) !== null;
  } catch {
    return false;
  }
}

/** The recorded parameters, or an empty object when there are none. */
export function readIframeParams(): IframeParams {
  if (typeof window === 'undefined') return {};

  let raw: string | null;
  try {
    raw = window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return {};
  }
  if (!raw) return {};

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return {};
  }
  if (typeof parsed !== 'object' || parsed === null) return {};

  const record = parsed as Record<string, unknown>;
  const params: IframeParams = {};
  for (const key of PARAM_KEYS) {
    const value = record[key];
    if (typeof value === 'string') params[key] = value;
  }
  return params;
}
